import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';

import { BehaviorSubject, filter, Observable, Subscription, switchMap, tap } from 'rxjs';
import { Moment } from 'moment-timezone';

import {
  Statistics,
  ServicesResourcesData,
  ServicesResourcesMetrics, PartiesGuestData,
} from '@shared/analytics/models';
import { CommonRadioButtonItem } from '@tablesready/common-ui';
import { ResourcesService, ServicesService } from '../../../services';
import { ServicesAndResourcesRadioItems } from '../../enums';
import { ILabels } from '../../analytics.interfaces';
import { AnalyticsService as AnalyticsSharedService } from '@shared/analytics/services';

@Component({
  selector: 'tablesready-services-and-resources-statistics',
  templateUrl: './services-and-resources-statistics.component.html',
  styleUrls: [],
})
export class ServicesAndResourcesStatisticsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() fromDate: string;
  @Input() toDate: string;
  @Input() labels: ILabels;
  @Input() serviceLabel: string;
  @Input() resourceLabel: string;
  @Input() timezone: string;
  @Input() servicesResourcesRadioItems: CommonRadioButtonItem[] = [];
  @Input() statistics: ServicesResourcesData[];

  @Input() loadTodayStats: () => Observable<Statistics>;
  @Input() simpleDateToMoment: (date: string) => Moment;
  @Input() formatDateToTimestamp: (date: string, text: string) => number;

  metrics: ServicesResourcesMetrics;
  selectedRadioItem = ServicesAndResourcesRadioItems.services;

  private readonly subject = new BehaviorSubject<SimpleChanges>(null);
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly analyticsSharedService: AnalyticsSharedService,
  ) {
    this.metrics = this.analyticsSharedService.getEmptyServicesResourcesMetrics();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.subject.next(changes);
  }

  ngOnInit(): void {
    const subjectSub = this.subject
      .pipe(
        filter(changes => {
          if (changes.statistics && changes.statistics.currentValue) {
            return true;
          } else {
            this.updateAnalytics();
            return false;
          }
        }),
        switchMap(() => this.loadTodayStats()),
        tap(stats => {
          this.statistics.push({
            date: stats.date,
            data: stats.servicesResourcesData,
          });
          this.updateAnalytics();
        }),
      ).subscribe();

    this.subscriptions.add(subjectSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  updateAnalytics(): void {
    const servicesAndResourcesStatistics = (this.statistics || [])
      .filter(
        record => this.simpleDateToMoment(record.date).valueOf() >= this.formatDateToTimestamp(this.fromDate, 'from')
          && this.simpleDateToMoment(record.date).valueOf() <= this.formatDateToTimestamp(this.toDate, 'to'),
      );

    this.loadAnalytics(servicesAndResourcesStatistics);
  }

  loadAnalytics(statistics: ServicesResourcesData[]): void {
    const metrics = this.analyticsSharedService.getEmptyServicesResourcesMetrics();
    for (const record of statistics) {
      for (const service of record.data.services) {
        this.analyticsSharedService
          .calcServicesAndResourcesSummaryStats(service, metrics.services);
      }

      for (const resource of record.data.resources) {
        this.analyticsSharedService
          .calcServicesAndResourcesSummaryStats(resource, metrics.resources);
      }

      for (const servicesAndResourcesRecord of record.data.servicesAndResources) {
        this.analyticsSharedService
          .calcServicesAndResourcesSummaryStats(servicesAndResourcesRecord, metrics.servicesAndResources, true);
      }
    }

    this.metrics = {...metrics};
  }

  get nameColumnHeaderValue(): string {
    switch (this.selectedRadioItem) {
      case ServicesAndResourcesRadioItems.services:
        return `${this.serviceLabel} name`;
      case ServicesAndResourcesRadioItems.resources:
        return `${this.resourceLabel} name`;
      case ServicesAndResourcesRadioItems.servicesAndResources:
        return `${this.serviceLabel} and ${this.resourceLabel} name`;
      default:
        return `${this.serviceLabel} name`;
    }
  }

  get noDataText(): string {
    switch (this.selectedRadioItem) {
      case ServicesAndResourcesRadioItems.services:
        return `Sorry, no available data for ${this.serviceLabel}.`;
      case ServicesAndResourcesRadioItems.resources:
        return `Sorry, no available data for ${this.resourceLabel}.`;
      case ServicesAndResourcesRadioItems.servicesAndResources:
        return `Sorry, no available data for ${this.serviceLabel} and ${this.resourceLabel}.`;
      default:
        return `Sorry, no available data for ${this.serviceLabel}.`;
    }
  }

  getName(id: string, comboId?: string): string {
    switch (this.selectedRadioItem) {
      case ServicesAndResourcesRadioItems.services:
        return this.servicesService.getById(id)?.name || 'N/A';
      case ServicesAndResourcesRadioItems.resources:
        return this.resourcesService.getById(id)?.name || 'N/A';
      case ServicesAndResourcesRadioItems.servicesAndResources:
        return `${this.servicesService.getById(id)?.name || 'N/A'} & ${this.resourcesService.getById(comboId)?.name || 'N/A'}`;
      default:
        return 'N/A';
    }
  }

  resetState(): void {
    this.selectedRadioItem = ServicesAndResourcesRadioItems.services;
  }
}
